import { useState, useEffect } from 'react';
import { config } from '../../auth/config';

export interface GalleryItem {
  id: number;
  title: string;
  description?: string;
  fileKey: string;
  fileName: string;
  fileType: 'image' | 'video';
  mimeType: string;
  fileSize: number;
  url: string;
  urlExpiry: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export const useGallery = (includeInactive = false) => {
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    fileType?: 'image' | 'video';
    search?: string;
    isActive?: boolean;
  }>({});

  const fetchGalleryItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = includeInactive ? '/api/gallery/admin' : '/api/gallery';
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      if (includeInactive && token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to fetch gallery items');
      }

      const data = await response.json();
      setGalleryItems(data.items || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching gallery items:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteGalleryItem = async (id: number) => {
    const token = localStorage.getItem('auth_token');
    if (!token) throw new Error('Authentication required');

    const response = await fetch(`${config.api.baseUrl}/api/gallery/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete gallery item');
    }

    // Remove from local state
    setGalleryItems(prev => prev.filter(item => item.id !== id));
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...galleryItems];

    // Apply file type filter
    if (filters.fileType) {
      filtered = filtered.filter(item => item.fileType === filters.fileType);
    }

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchTerm) ||
        (item.description && item.description.toLowerCase().includes(searchTerm))
      );
    }

    // Apply active status filter (admin only)
    if (includeInactive && filters.isActive !== undefined) {
      filtered = filtered.filter(item => item.isActive === filters.isActive);
    }

    setFilteredItems(filtered);
  }, [galleryItems, filters, includeInactive]);

  useEffect(() => {
    fetchGalleryItems();
  }, [includeInactive]);

  return { 
    galleryItems: filteredItems, 
    loading, 
    error, 
    filters,
    setFilters,
    refetch: fetchGalleryItems,
    deleteGalleryItem
  };
};
